import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';
import { LoginResponse } from '@/types';
import { LayoutDashboard, Mail, Lock, Eye, EyeOff, LogIn, ShieldCheck, AlertCircle, Loader } from 'lucide-react';
import styles from './LoginPage.module.css';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    remember: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email richiesta';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email non valida';
    }

    if (!formData.password) {
      newErrors.password = 'Password richiesta';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La password deve contenere almeno 6 caratteri';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // Handle form submit
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await api.post<LoginResponse>('/auth/login', {
        email: formData.email,
        password: formData.password,
        rememberMe: formData.remember,
      });

      // With HttpOnly cookie authentication, the token is automatically stored
      // in a secure cookie by the browser. We only need to store user data.
      const { user } = response.data;

      // Update auth context (only user data, token is in HttpOnly cookie)
      login(user);

      // Redirect to report
      navigate('/report');
    } catch (error: any) {
      console.error('Login error:', error);

      // Handle different error types
      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.detail;

        if (status === 401) {
          setErrors({ general: 'Email o password non corretti' });
        } else if (status === 400) {
          setErrors({ general: message || 'Dati non validi' });
        } else if (status === 403) {
          setErrors({ general: 'Accesso negato. Account non attivo.' });
        } else {
          setErrors({ general: 'Errore del server. Riprova più tardi.' });
        }
      } else if (error.request) {
        // Request was made but no response
        setErrors({ general: 'Errore di connessione. Verifica la tua connessione internet.' });
      } else {
        // Something else happened
        setErrors({ general: 'Errore imprevisto. Riprova.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        {/* Header */}
        <div className={styles.loginHeader}>
          <div className={styles.loginLogo}>
            <LayoutDashboard size={40} />
          </div>
          <h1 className={styles.loginTitle}>Saldo Perfetto</h1>
          <p className={styles.loginSubtitle}>Entrate & Uscite</p>
        </div>

        {/* Body */}
        <div className={styles.loginBody}>
          {/* Alert Error */}
          {errors.general && (
            <div className={styles.alertError}>
              <AlertCircle size={20} />
              <span>{errors.general}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className={styles.inputGroup}>
              <label htmlFor="email">Email</label>
              <div className={styles.inputWrapper}>
                <Mail className={styles.inputIcon} size={20} />
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="tua@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isLoading}
                  autoComplete="email"
                  autoFocus
                  className={errors.email ? styles.inputError : ''}
                />
              </div>
              {errors.email && <div className={styles.errorText}>{errors.email}</div>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">Password</label>
              <div className={styles.inputWrapper}>
                <Lock className={styles.inputIcon} size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  autoComplete="current-password"
                  className={errors.password ? styles.inputError : ''}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <div className={styles.errorText}>{errors.password}</div>}
            </div>

            <div className={styles.rememberForgot}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                <span>Ricordami</span>
              </label>
            </div>

            <button type="submit" className={styles.loginBtn} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader className={styles.spin} size={20} />
                  Accesso in corso...
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Accedi
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Version & Credits */}
      <div className={styles.credits}>
        <p>
          <ShieldCheck size={16} style={{ verticalAlign: 'middle' }} />
          {' '}Connessione sicura protetta
        </p>
        <p className={styles.creditsVersion}>v1.0.0 • © 2025 Saldo Perfetto</p>
      </div>
    </div>
  );
};
